/**
 * Shared hook for org-scoped curated league data
 *
 * Single source of truth for league options across all filter surfaces.
 * Uses listMyOrganizationsLeagues (curated, org-scoped) instead of
 * listLeagues (uncurated, system-wide). Formats leagues as
 * "${country} - ${name}", filters empty names, and sorts alphabetically.
 * @module hooks/useOrganizationLeagues
 */

import { useMemo } from "react";

import {
  RosterManagementLeagueFragment,
  useRosterManagementListMyOrganizationsLeaguesQuery,
} from "@/generated/graphql";

import type { LeaguePresetOption } from "@/features/player-filter/utils/leaguePresets";

/** Options for the useOrganizationLeagues hook */
interface UseOrganizationLeaguesOptions {
  /** Skip the query (e.g. when auth is not available) */
  readonly skip?: boolean;
}

/** Return type for the useOrganizationLeagues hook */
interface UseOrganizationLeaguesResult {
  /** Formatted league fragments for CustomMultiSelectContainer consumers */
  readonly leagues: RosterManagementLeagueFragment[];
  /** Formatted league options for FilterChip consumers */
  readonly leagueOptions: readonly LeaguePresetOption[];
  /** Whether the query is still loading */
  readonly loading: boolean;
}

/**
 * Fetches and formats org-scoped curated leagues.
 * @param options - Hook options
 * @param options.skip - Whether to skip the query
 * @returns Formatted league data in two shapes for different consumers
 */
const useOrganizationLeagues = (
  options?: UseOrganizationLeaguesOptions
): UseOrganizationLeaguesResult => {
  const { data, loading } = useRosterManagementListMyOrganizationsLeaguesQuery({
    variables: { first: 1000 },
    skip: options?.skip,
  });

  const leagues = useMemo((): RosterManagementLeagueFragment[] => {
    const edges = data?.listMyOrganizationsLeagues?.edges ?? [];
    return [...edges]
      .filter(e => e.node.name !== "")
      .map(
        e =>
          ({
            id: e.node.id,
            name: e.node.country
              ? `${e.node.country} - ${e.node.name}`
              : e.node.name,
          }) as RosterManagementLeagueFragment
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data?.listMyOrganizationsLeagues?.edges]);

  const leagueOptions = useMemo((): readonly LeaguePresetOption[] => {
    const nodes = data?.listMyOrganizationsLeagues?.edges ?? [];
    return nodes
      .filter(edge => edge.node.name !== "")
      .map(edge => ({
        id: edge.node.id,
        label: edge.node.country
          ? `${edge.node.country} - ${edge.node.name}`
          : edge.node.name,
        country: edge.node.country,
        leagueName: edge.node.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data?.listMyOrganizationsLeagues?.edges]);

  return { leagues, leagueOptions, loading };
};

export default useOrganizationLeagues;
